import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Shield, LogOut, ChevronDown, UserCircle, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logo from "@/assets/4fox-logo.png";
import { GlobalSearch } from "./GlobalSearch";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [overflowItems, setOverflowItems] = useState<string[]>([]);
  const navRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, isAdmin, loading, signOut } = useAuth();

  const allNavItems = [
    { label: "Home", path: "/" },
    { label: "Services", path: "/services", onClick: "handleServicesClick" },
    { label: "Insights", path: "/insights" },
    { label: "Threat Intelligence", path: "/threat-intelligence" },
    { label: "Resources", path: "/resources" },
    { label: "Testimonials", path: "/testimonials" },
    { label: "About", path: "/about" },
    { label: "FAQ", path: "/faq" },
    { label: "Contact", path: "/contact" },
  ];

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsOpen(false);
  };

  const handleServicesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const servicesSection = document.getElementById('services');
      if (servicesSection) {
        servicesSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    setIsOpen(false);
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (!navRef.current || window.innerWidth < 768) {
        setOverflowItems([]);
        return;
      }
      
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        if (!navRef.current) return;
        
        const navContainer = navRef.current;
        const navContainerRect = navContainer.getBoundingClientRect();
        
        // Get the parent navbar container
        const navbar = navContainer.closest('.flex') as HTMLElement;
        if (!navbar) return;
        
        // Get actual widths of logo and auth sections
        const logo = navbar.querySelector('a[href="/"]') as HTMLElement;
        const authSection = navbar.querySelector('.flex-shrink-0') as HTMLElement;
        
        if (!logo || !authSection) return;
        
        const logoRect = logo.getBoundingClientRect();
        const authRect = authSection.getBoundingClientRect();
        const navbarRect = navbar.getBoundingClientRect();
        
        // Calculate actual available width for navigation items
        const availableWidth = navbarRect.width - logoRect.width - authRect.width - 80; // 80px for gaps
        
        // Check if the nav container's content is overflowing
        const isOverflowing = navContainer.scrollWidth > availableWidth;
        
        if (!isOverflowing) {
          // Everything fits, show all items
          setOverflowItems([]);
        } else {
          // Start collapsing items from the end until they fit
          for (let i = allNavItems.length - 1; i >= 0; i--) {
            const testOverflow = allNavItems.slice(i).map(item => item.label);
            setOverflowItems(testOverflow);
            
            // Give DOM time to update and check if it fits now
            setTimeout(() => {
              if (navRef.current && navRef.current.scrollWidth <= availableWidth) {
                return;
              }
            }, 0);
            
            // If we've collapsed enough items, stop
            if (i <= allNavItems.length - 3) break;
          }
        }
      });
    };

    // Initial check with delay to ensure DOM is ready
    const timeoutId = setTimeout(checkOverflow, 100);
    window.addEventListener('resize', checkOverflow);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  const visibleItems = overflowItems.length > 0 
    ? allNavItems.filter(item => !overflowItems.includes(item.label))
    : allNavItems;
  
  const moreItems = overflowItems.length > 0 
    ? allNavItems.filter(item => overflowItems.includes(item.label))
    : [];


  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm border-b border-white/10">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <a href="/" className="flex items-center flex-shrink-0">
            <img src={logo} alt="4FOX Security" className="h-10 object-contain" width="140" height="40" />
          </a>

          {/* Desktop Navigation */}
          <div ref={navRef} className="hidden md:flex items-center gap-4 flex-1 justify-start ml-8">
            {visibleItems.map((item) => {
              if (item.label === "Home") {
                return (
                  <a
                    key={item.label}
                    href="/"
                    onClick={handleHomeClick}
                    className="text-white/90 hover:text-white transition-colors text-base font-medium cursor-pointer whitespace-nowrap"
                  >
                    {item.label}
                  </a>
                );
              }
              if (item.label === "Services") {
                return (
                  <a
                    key={item.label}
                    href="/#services"
                    onClick={handleServicesClick}
                    className="text-white/90 hover:text-white transition-colors text-base font-medium cursor-pointer whitespace-nowrap"
                  >
                    {item.label}
                  </a>
                );
              }
              return (
                <Link 
                  key={item.label}
                  to={item.path} 
                  className="text-white/90 hover:text-white transition-colors text-base font-medium whitespace-nowrap"
                >
                  {item.label}
                </Link>
              );
            })}
            
            {/* More Dropdown for Overflow Items */}
            {moreItems.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger className="text-white/90 hover:text-white transition-colors text-base font-medium flex items-center gap-1 bg-transparent border-0 p-0 whitespace-nowrap">
                  More <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-primary/95 backdrop-blur-sm border-white/10">
                  {moreItems.map((item) => (
                    <DropdownMenuItem key={item.label} asChild>
                      <Link to={item.path} className="w-full text-white hover:bg-white/10">
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Auth and CTA Buttons */}
          <div className="hidden md:flex items-center gap-4 flex-shrink-0">
            <button
              onClick={() => setIsSearchOpen(true)}
              className="text-white/90 hover:text-white transition-colors flex items-center gap-2 group"
              aria-label="Search"
              title="Search"
            >
              <Search className="w-5 h-5" />
            </button>
            {user && (
              <Link to="/account" className="text-white/90 hover:text-white transition-colors text-base font-medium flex items-center gap-1">
                <UserCircle className="w-4 h-4" />
                Account
              </Link>
            )}
            {user && isAdmin && !loading && (
              <Link to="/admin" className="text-white/90 hover:text-white transition-colors text-base font-medium flex items-center gap-1">
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            )}
            {user ? (
              <Button 
                variant="outline" 
                onClick={() => signOut()}
                className="gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            ) : (
              <Link to="/auth">
                <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                  Login
                </Button>
              </Link>
            )}
            <a 
              href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="default" className="bg-accent hover:bg-accent/90 whitespace-nowrap">
                Book Free Consultation
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 space-y-4">
            <a
              href="/"
              onClick={handleHomeClick}
              className="block text-white/90 hover:text-white transition-colors py-2 cursor-pointer"
            >
              Home
            </a>
            <a
              href="/#services"
              onClick={handleServicesClick}
              className="block text-white/90 hover:text-white transition-colors py-2 cursor-pointer"
            >
              Services
            </a>
            <Link
              to="/insights"
              className="block text-white/90 hover:text-white transition-colors py-2"
              onClick={() => setIsOpen(false)}
            >
              Insights
            </Link>
            <Link
              to="/threat-intelligence"
              className="block text-white/90 hover:text-white transition-colors py-2"
              onClick={() => setIsOpen(false)}
            >
              Threat Intelligence
            </Link>
            <Link
              to="/resources"
              className="block text-white/90 hover:text-white transition-colors py-2"
              onClick={() => setIsOpen(false)}
            >
              Resources
            </Link>
            <Link
              to="/testimonials"
              className="block text-white/90 hover:text-white transition-colors py-2"
              onClick={() => setIsOpen(false)}
            >
              Testimonials
            </Link>
            <Link
              to="/about"
              className="block text-white/90 hover:text-white transition-colors py-2"
              onClick={() => setIsOpen(false)}
            >
              About
            </Link>
            <Link
              to="/faq"
              className="block text-white/90 hover:text-white transition-colors py-2"
              onClick={() => setIsOpen(false)}
            >
              FAQ
            </Link>
            <Link
              to="/contact"
              className="block text-white/90 hover:text-white transition-colors py-2"
              onClick={() => setIsOpen(false)}
            >
              Contact
            </Link>
            <button
              onClick={() => {
                setIsSearchOpen(true);
                setIsOpen(false);
              }}
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors py-2 w-full text-left"
            >
              <Search className="w-4 h-4" />
              Search
            </button>
            {user && (
              <Link
                to="/account"
                className="flex items-center gap-2 text-white/90 hover:text-white transition-colors py-2"
                onClick={() => setIsOpen(false)}
              >
                <UserCircle className="w-4 h-4" />
                Account
              </Link>
            )}
            {user && isAdmin && !loading && (
              <Link
                to="/admin"
                className="flex items-center gap-2 text-white/90 hover:text-white transition-colors py-2"
                onClick={() => setIsOpen(false)}
              >
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            )}
            {user ? (
              <Button 
                variant="outline" 
                onClick={() => {
                  signOut();
                  setIsOpen(false);
                }}
                className="w-full gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            ) : (
              <Link to="/auth" onClick={() => setIsOpen(false)} className="block">
                <Button variant="outline" className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20">
                  Login
                </Button>
              </Link>
            )}
            <a 
              href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Button variant="default" className="w-full bg-accent hover:bg-accent/90">
                Book Free Consultation
              </Button>
            </a>
          </div>
        )}
      </div>
      <GlobalSearch open={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </nav>
  );
};

export default Navbar;
